import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ReceiptEditForm } from "../components/ReceiptEditForm";
import { ReceiptStatusBadge } from "../components/ReceiptStatusBadge";
import { api } from "../lib/api";
import type { ReceiptDetailData, ReceiptStructuredResult } from "../lib/api";
import { AppError, logError } from "../lib/errors";

type FormValues = Partial<ReceiptStructuredResult> & { userNotes?: string };

function formatExpiresAt(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function toFormValues(data: ReceiptDetailData): FormValues {
  const r = data.structuredResult;
  return {
    merchantName: r?.merchantName ?? "",
    receiptDate: r?.receiptDate ?? "",
    totalAmount: r?.totalAmount ?? "",
    currency: r?.currency ?? "JPY",
    taxAmount: r?.taxAmount ?? "",
    paymentMethod: r?.paymentMethod ?? "",
    invoiceNumber: r?.invoiceNumber ?? "",
    accountCategory: r?.accountCategory ?? "",
    summary: r?.summary ?? "",
    userNotes: data.userNotes ?? "",
  };
}

type FieldRow = { label: string; value: string | null | undefined };

function DetailField({ label, value }: FieldRow) {
  return (
    <div className="flex justify-between gap-2 border-b border-[#F0E4DA] py-2.5 last:border-0">
      <span className="min-w-[6rem] shrink-0 text-xs text-[#9C867D]">{label}</span>
      <span className="text-right text-sm font-bold text-[#4A3E39]">{value || "—"}</span>
    </div>
  );
}


export default function ReceiptDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<ReceiptDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sourceImageUrl, setSourceImageUrl] = useState<string | null>(null);
  const [sourceImageExpiresAt, setSourceImageExpiresAt] = useState<string | null>(null);
  const [sourceImageLoading, setSourceImageLoading] = useState(false);
  const [sourceImageError, setSourceImageError] = useState<string | null>(null);
  const [sourceImageUnavailable, setSourceImageUnavailable] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.getReceiptDetail(jobId);
      setData(result);
    } catch (e) {
      setError(e instanceof AppError ? e.message : "データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (values: FormValues) => {
    if (!jobId) return;
    setSaving(true);
    setActionError(null);
    try {
      await api.updateReceiptJob(jobId, values);
      setEditing(false);
      await load();
    } catch (e) {
      setActionError(e instanceof AppError ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async () => {
    if (!jobId) return;
    setReviewing(true);
    setActionError(null);
    try {
      const result = await api.reviewReceiptJob(jobId);
      setData((prev) =>
        prev ? { ...prev, needsReview: result.needsReview, reviewedAt: result.reviewedAt } : prev
      );
    } catch (e) {
      setActionError(e instanceof AppError ? e.message : "確認済み処理に失敗しました");
    } finally {
      setReviewing(false);
    }
  };

  const handleShowSourceImage = async () => {
    if (!jobId || sourceImageUnavailable) return;

    setSourceImageLoading(true);
    setSourceImageError(null);
    try {
      const response = await api.getReceiptSourceImage(jobId);
      setSourceImageUrl(response.url);
      setSourceImageExpiresAt(response.expiresAt);
    } catch (e) {
      logError(e);
      if (e instanceof AppError && e.meta.status === 410) {
        setSourceImageUnavailable(true);
        setSourceImageError("原本画像の保管期限が過ぎて削除されています。");
      } else {
        setSourceImageError(e instanceof AppError ? e.message : "画像の取得に失敗しました");
      }
    } finally {
      setSourceImageLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!jobId) return;
    setDeleting(true);
    setActionError(null);
    try {
      await api.deleteReceiptJob(jobId);
      navigate("/receipt/history", { replace: true });
    } catch (e) {
      logError(e);
      setActionError(e instanceof AppError ? e.message : "削除に失敗しました");
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = () => {
    navigate("/receipt");
  };

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)]">
        <p className="text-sm text-[#9C867D]">読み込み中…</p>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39]">
        <div className="mx-auto w-full max-w-xl">
          <BackHeader />
          <div className="mt-10 text-center">
            <p className="text-sm text-[#AF3B2E]">{error ?? "データが見つかりません"}</p>
          </div>
        </div>
      </main>
    );
  }

  const r = data.structuredResult;

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-xl">
        <BackHeader />

        <section className="mt-5 w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-lg font-black tracking-tight text-[#4A3E39]">
              {r?.merchantName || "領収書詳細"}
            </h1>
            <ReceiptStatusBadge item={data} />
          </div>

          {/* State D: Failed */}
          {data.relayStatus === "failed" && (
            <div className="mb-4 rounded-2xl bg-red-50 p-4">
              <p className="text-sm font-bold text-red-700">処理中にエラーが発生しました</p>
              {data.failedError && (
                <p className="mt-1 text-xs text-red-600">{data.failedError}</p>
              )}
              <button
                type="button"
                onClick={handleRetry}
                className="mt-3 rounded-full bg-[#A67C52] px-4 py-1.5 text-sm font-bold text-white hover:bg-[#7C5A4E]"
              >
                もう一度試す
              </button>
            </div>
          )}

          {/* State A/C: Completed */}
          {data.relayStatus === "completed" && !editing && (
            <>
              <div className="mb-4">
                <DetailField label="日付" value={r?.receiptDate} />
                <DetailField label="合計金額" value={r?.totalAmount ? `${r.totalAmount} ${r.currency ?? ""}`.trim() : null} />
                <DetailField label="税額" value={r?.taxAmount} />
                <DetailField label="支払方法" value={r?.paymentMethod} />
                <DetailField label="インボイス番号" value={r?.invoiceNumber} />
                <DetailField label="勘定科目" value={r?.accountCategory} />
                <DetailField label="概要" value={r?.summary} />
                {data.userNotes && <DetailField label="メモ" value={data.userNotes} />}
                {data.processedAt && <DetailField label="処理日時" value={formatDateTime(data.processedAt)} />}
                {data.reviewedAt && <DetailField label="確認日時" value={formatDateTime(data.reviewedAt)} />}
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleShowSourceImage}
                  disabled={sourceImageLoading || sourceImageUnavailable}
                  className="inline-flex items-center gap-1 rounded-full border border-[#A67C52] px-3 py-1.5 text-xs font-bold text-[#A67C52] hover:bg-[#F5EDE8] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sourceImageLoading ? "取得中..." : "原本画像を表示"}
                </button>
              </div>

              {sourceImageError && (
                <p className="mb-3 text-xs text-[#AF3B2E]">{sourceImageError}</p>
              )}

              {actionError && (
                <p className="mb-3 text-xs text-[#AF3B2E]">{actionError}</p>
              )}

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => { setEditing(true); setActionError(null); }}
                  className="w-full rounded-full border border-[#A67C52] py-2.5 text-sm font-bold text-[#A67C52] hover:bg-[#F5EDE8]"
                >
                  内容を修正する
                </button>
                {data.needsReview && (
                  <button
                    type="button"
                    onClick={handleReview}
                    disabled={reviewing}
                    className="w-full rounded-full bg-[#A67C52] py-2.5 text-sm font-bold text-white hover:bg-[#7C5A4E] disabled:opacity-60"
                  >
                    {reviewing ? "処理中…" : "確認済みにする"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="w-full rounded-full border border-[#C9867D] py-2.5 text-sm font-bold text-[#A1493E] hover:bg-[#FFF0EE]"
                >
                  この領収書を削除
                </button>
              </div>
            </>
          )}

          {/* State B: Edit form */}
          {data.relayStatus === "completed" && editing && (
            <>
              {actionError && (
                <p className="mb-3 text-xs text-[#AF3B2E]">{actionError}</p>
              )}
              <ReceiptEditForm
                initial={toFormValues(data)}
                saving={saving}
                onSave={handleSave}
                onCancel={() => { setEditing(false); setActionError(null); }}
              />
            </>
          )}

          {/* State: still processing */}
          {data.relayStatus === "accepted" && (
            <p className="py-4 text-center text-sm text-[#9C867D]">
              処理中です。しばらくしてから再度ご確認ください。
            </p>
          )}
        </section>
      </div>

      {sourceImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6">
          <div className="max-h-full w-full max-w-4xl overflow-auto rounded-[2rem] bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              {sourceImageExpiresAt && (
                <p className="text-sm font-bold text-[#7C5A4E]">
                  期限: {formatExpiresAt(sourceImageExpiresAt)}
                </p>
              )}
              <button
                type="button"
                onClick={() => setSourceImageUrl(null)}
                aria-label="閉じる"
                className="ml-auto rounded-full border border-[#D8C0AF] bg-white px-3 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                閉じる
              </button>
            </div>
            <img src={sourceImageUrl} alt="原本画像" className="mx-auto max-h-[75vh] max-w-full rounded-2xl object-contain" />
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-[2rem] bg-white p-5 shadow-lg">
            <h2 className="text-lg font-black text-[#5D4D46]">削除しますか？</h2>
            <p className="mt-3 text-sm leading-6 text-[#8A746A]">
              「{r?.merchantName || "この領収書"}」を削除すると元に戻せません。
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className="rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-full bg-[#A1493E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#8C3A33] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? "削除中..." : "削除する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function BackHeader() {
  return (
    <header className="mb-6 flex items-center justify-between">
      <Link
        to="/receipt/history"
        className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]"
      >
        ←戻る
      </Link>
      <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">領収書詳細</p>
      <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
        WizMate
      </Link>
    </header>
  );
}
